import { useEffect, useRef, useState } from "react";
import { diag, since } from "../lib/diag";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/use-auth"; // Adjust path as needed
import LoadingScreen from "./loading-screen";
import UserNav from "../components/user-nav"; // Adjust path as needed
import { Link } from "react-router-dom";
import DesktopNav from "../components/desktop-nav"; // Adjust path as needed
import { cn } from "../lib/utils"; // Adjust path as needed
import {
  onSnapshot,
  query,
  collection,
  where,
  getDocs,
} from "@firebase/firestore";
import { db } from "../lib/firebase"; // Adjust path as needed
import { PoliciesModal } from "./policies-modal";
import { VideoUploadBar } from "./video-upload-bar";
import { PausedNotice } from "./paused-notice";
import { PAUSED_PATHS, hasActiveSubscription, isLimitedByPause } from "../lib/subscription";
import { INACTIVE_PATHS, isBlockedForInactivity } from "../lib/inactivity";
import { InactivityDialog, InactivityNotice } from "./inactivity-notice";

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, loading, setUnreadChatCount, setUnreadCountsMap } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Diagnostics: which screen was opened and how long it took to draw.
  const navStartedAt = useRef(performance.now());
  const lastPath = useRef(location.pathname);
  if (lastPath.current !== location.pathname) {
    lastPath.current = location.pathname;
    navStartedAt.current = performance.now();
  }
  useEffect(() => {
    diag("screen", { path: location.pathname, renderMs: since(navStartedAt.current) });
  }, [location.pathname]);

  // Inactivity block (lib/inactivity.ts): the popup shows once when the app
  // opens blocked, and again each time a blocked screen is opened.
  const blockedForInactivity = isBlockedForInactivity(user);
  const [showInactivityPopup, setShowInactivityPopup] = useState(false);
  const inactivityPopupShown = useRef(false);
  useEffect(() => {
    if (!blockedForInactivity) {
      inactivityPopupShown.current = false;
      return;
    }
    if (!inactivityPopupShown.current) {
      inactivityPopupShown.current = true;
      diag("inactivity block shown");
      setShowInactivityPopup(true);
    }
  }, [blockedForInactivity]);

  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate("/", { replace: true });
      return;
    }

    const isSubscriptionPage = location.pathname.startsWith("/subscribe");
    // Only check subscription for the 'user' role
    if (user.role === "user") {
      if (!hasActiveSubscription(user)) {
        if (!isSubscriptionPage) {
          navigate("/subscribe", { replace: true });
          return;
        }
      }
      // Paused: only the feed and the chat (see lib/subscription.ts).
      if (isLimitedByPause(user) && !PAUSED_PATHS.some((path) => location.pathname.startsWith(path))) {
        navigate("/feed", { replace: true });
        return;
      }
      // Blocked for inactivity: only the chat (see lib/inactivity.ts).
      if (blockedForInactivity && !INACTIVE_PATHS.some((path) => location.pathname.startsWith(path))) {
        navigate("/chat", { replace: true });
        if (inactivityPopupShown.current) setShowInactivityPopup(true);
        return;
      }
    }
  }, [user, loading, navigate, location.pathname, blockedForInactivity]);

  useEffect(() => {
    if (!user) return;

    // Unread counts per group, reused while a group's lastMessageAt/lastRead
    // are unchanged. The groups snapshot fires on every new message in any
    // group, and re-counting every group each time was a lot of reads.
    const countCache = new Map<string, { key: string; count: number }>();
    // Snapshots are handled asynchronously; a slow older one must not
    // overwrite the counts from a newer one (stuck unread badge).
    let latestSnapshot = 0;

    // Set up the real-time listener for unread chat counts.
    const q = query(
      collection(db, "groups"),
      where("members", "array-contains", user.id)
    );
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const snapshotNumber = ++latestSnapshot;
      let totalUnreadCount = 0;
      const newUnreadMap: Record<string, number> = {};
      const groupPromises = snapshot.docs.map(async (groupDoc) => {
        try {
          const data = groupDoc.data();
          const lastReadTimestamp = data.lastRead?.[user.id] || new Date(0).toISOString();
          const lastMessageAt = data.lastMessageAt || new Date(0).toISOString();
          
          // Skip expensive Firebase query if there's clearly no new messages
          if (lastMessageAt <= lastReadTimestamp) {
            newUnreadMap[groupDoc.id] = 0;
            return 0;
          }

          const cacheKey = `${lastMessageAt}|${lastReadTimestamp}`;
          const cached = countCache.get(groupDoc.id);
          if (cached && cached.key === cacheKey) {
            newUnreadMap[groupDoc.id] = cached.count;
            return cached.count > 0 ? 1 : 0;
          }

          const messagesRef = collection(db, "groups", groupDoc.id, "messages");
          const unreadQuery = query(
            messagesRef,
            where("createdAt", ">", lastReadTimestamp)
          );
          const unreadSnapshot = await getDocs(unreadQuery);

          const unreadMessagesFromOthers = unreadSnapshot.docs.filter(
            (doc) => doc.data().senderId !== user.id
          );

          const count = unreadMessagesFromOthers.length;
          countCache.set(groupDoc.id, { key: cacheKey, count });
          newUnreadMap[groupDoc.id] = count;
          return count > 0 ? 1 : 0;
        } catch (error) {
          console.warn(`Failed to fetch unread for group ${groupDoc.id}`, error);
          newUnreadMap[groupDoc.id] = 0;
          return 0;
        }
      });

      const unreadFlags = await Promise.all(groupPromises);
      if (snapshotNumber !== latestSnapshot) return;
      totalUnreadCount = unreadFlags.reduce((acc: number, flag) => acc + flag, 0);

      setUnreadChatCount(totalUnreadCount);
      setUnreadCountsMap(newUnreadMap);

      // Sync iOS home screen badge with the real in-app unread count.
      // Uses @capawesome/capacitor-badge which calls the native iOS badge API.
      // Dynamic import so the web/mobile build doesn't fail when the native
      // plugin package is not present in node_modules.
      try {
        // @ts-ignore – package is intentionally absent; runtime catch handles missing plugin
        const { Badge } = await import("@capawesome/capacitor-badge");
        await Badge.set({ count: totalUnreadCount });
      } catch {
        // Not supported on this platform — silently ignore
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, setUnreadChatCount, setUnreadCountsMap]);

  if (loading || !user) {
    return <LoadingScreen />;
  }

  // If a user is expired and on the subscribe page, don't render the main layout.
  if (
    user.role === "user" &&
    !hasActiveSubscription(user) &&
    location.pathname.startsWith("/subscribe")
  ) {
    return <main>{children}</main>;
  }

  const isChatPage = location.pathname === "/chat";

  return (
    <div
      className={cn(
        "flex w-full flex-col",
        isChatPage ? "h-screen" : "min-h-screen"
      )}
    >
      <header className="sticky top-0 flex h-16 items-center gap-4 border-b border-border/20 bg-[#06141B] px-4 md:px-6 z-50 shrink-0 pt-[env(safe-area-inset-top)]" style={{ height: 'calc(4rem + env(safe-area-inset-top))' }}>
        <Link
          to="/feed"
          className="hidden md:flex items-center gap-2 font-semibold text-primary"
        >
          {/* Replace with your React image component */}
          <img
            src="/img/logo.png"
            alt="NemaNeide Logo"
            width={150}
            height={40}
            className="object-contain"
          />
        </Link>
        <div className="flex-1 flex justify-center">
          <DesktopNav />
        </div>
        <div className="flex items-center gap-2">
          <UserNav />
        </div>
      </header>
      <VideoUploadBar />
      {isLimitedByPause(user) && <PausedNotice />}
      {blockedForInactivity && <InactivityNotice />}
      <main
        className="flex flex-1 flex-col min-h-0"
      >
        {isChatPage ? (
          // No animation for chat — avoids the lag caused by waiting for exit animation
          <div className="h-full w-full overflow-hidden">
            {children}
          </div>
        ) : (
          // New pages fade in with a CSS animation. The old page is not kept
          // around to fade out first: waiting for that added a delay to every
          // navigation, and if the animation stalled the screen stayed blank.
          <div key={location.pathname} className="page-enter h-full w-full p-4 md:gap-8 md:p-8">
            {children}
          </div>
        )}
      </main>
      <PoliciesModal />
      <InactivityDialog open={blockedForInactivity && showInactivityPopup} onOpenChange={setShowInactivityPopup} />
    </div>
  );
}
